import { Action, Module, VuexModule } from 'vuex-class-modules'
import store from '@/store/index'
import vehicle from '@/services/vehicle'
import split from '@/store/modules/split'
import alert from '@/store/modules/alert'
import { VehicleAmenityAlert } from '@/models/dto/Vehicle'
import { SplitFeatureFlag, VehicleAmenityAlertType } from '@/utils/enum'
import {
  buildAmenityAlert,
  clearVehicleAmenityAlerts,
  loadVehicleAmenityAlerts,
  mergeAmenityAlerts,
  saveVehicleAmenityAlerts,
} from '@/utils/vehicleAmenityAlerts'

/** Minimum time between amenity-alert refetches. */
export const AMENITY_ALERTS_FETCH_TTL_MS = 5 * 60 * 1000

@Module({ generateMutationSetters: true })
class VehicleAmenityAlertsModule extends VuexModule {
  _pending: VehicleAmenityAlert[] = loadVehicleAmenityAlerts()
  _enqueuedAlertIds: Set<string> = new Set()
  _lastFetchedAt: number | null = null

  get pending(): VehicleAmenityAlert[] {
    return this._pending
  }

  get lastFetchedAt(): number | null {
    return this._lastFetchedAt
  }

  isFetchStale(): boolean {
    if (this._lastFetchedAt == null) {
      return true
    }
    return Date.now() - this._lastFetchedAt >= AMENITY_ALERTS_FETCH_TTL_MS
  }

  @Action
  async saveAmenityAlertsIfEnabled(): Promise<VehicleAmenityAlert[]> {
    const isEnabled = await split.isFeatureEnabled(
      SplitFeatureFlag.AmenitiesExceptions
    )
    if (!isEnabled) {
      return []
    }
    return this.saveAmenityAlerts()
  }

  @Action
  async saveAmenityAlerts(): Promise<VehicleAmenityAlert[]> {
    if (!this.isFetchStale()) {
      return []
    }

    this._lastFetchedAt = Date.now()

    try {
      const { data } = await vehicle.getAmenityAlerts()
      const merged = mergeAmenityAlerts(this._pending, data)

      this._pending = merged
      saveVehicleAmenityAlerts(merged)

      return data
    } catch (error) {
      // Do not let amenity-alert failures bubble into router auth handling.
      console.warn('Error fetching vehicle amenity alerts:', error)
      this._lastFetchedAt = null
      return []
    }
  }

  @Action
  async displayPendingAlertsIfEnabled(): Promise<void> {
    const isEnabled = await split.isFeatureEnabled(
      SplitFeatureFlag.AmenitiesExceptions
    )
    if (!isEnabled) {
      return
    }

    const alertsToShow = this._pending.filter(
      (a) => !this._enqueuedAlertIds.has(a.id)
    )
    if (alertsToShow.length === 0) {
      return
    }

    const alertsByType: Partial<
      Record<VehicleAmenityAlertType, VehicleAmenityAlert[]>
    > = {}
    alertsToShow.forEach((a) => {
      alertsByType[a.vehicleAmenityAlertType] = [
        ...(alertsByType[a.vehicleAmenityAlertType] ?? []),
        a,
      ]
    })
    for (const [alertType, alerts] of Object.entries(alertsByType) as [
      VehicleAmenityAlertType,
      VehicleAmenityAlert[],
    ][]) {
      const alertObject = buildAmenityAlert(alertType, alerts)
      if (alertObject) {
        const alertIds = alerts.map((a) => a.id)
        alert.add({
          ...alertObject,
          onDismiss: () => this.dismissAmenityAlerts(alertIds),
        })
        alertIds.forEach((id) => this._enqueuedAlertIds.add(id))
      }
    }
  }

  @Action
  dismissAmenityAlerts(alertIds: string[]): void {
    if (!alertIds.length) {
      return
    }
    const ids = new Set(alertIds)
    this._pending = this._pending.filter((alert) => !ids.has(alert.id))
    ids.forEach((id) => this._enqueuedAlertIds.delete(id))
    saveVehicleAmenityAlerts(this._pending)
  }

  @Action
  reset(): void {
    this._pending = []
    this._enqueuedAlertIds = new Set()
    this._lastFetchedAt = null
    clearVehicleAmenityAlerts()
  }
}

export default new VehicleAmenityAlertsModule({
  store,
  name: 'vehicleAmenityAlerts',
})
